'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import localFont from 'next/font/local';
import { useSession } from '@/lib/auth-client';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Container } from '../ReferenceChrome';

const heroDisplay = localFont({
  src: [
    { path: '../../../../public/fonts/fraunces-regular.woff2', weight: '400', style: 'normal' },
    { path: '../../../../public/fonts/fraunces-600.woff2', weight: '600', style: 'normal' },
  ],
});

/* ─── Table data ─────────────────────────────────────────────────────────────── */

type CellValue = string | boolean | null;

interface FeatureGroup {
  group: string;
  rows: Array<{ label: string; base: CellValue; pro: CellValue }>;
}

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    group: 'Usage',
    rows: [
      { label: 'Sites', base: '1', pro: '3' },
      { label: 'AI credits per month', base: '100', pro: '300' },
    ],
  },
  {
    group: 'Features',
    rows: [
      { label: 'Competitor keyword gap discovery', base: true, pro: true },
      { label: 'Keyword research', base: true, pro: true },
      { label: 'Realtime market data', base: true, pro: true },
      { label: 'Backlink analysis', base: true, pro: true },
      { label: 'Search Console connection', base: true, pro: true },
      { label: 'AI agent chat', base: true, pro: true },
    ],
  },
  {
    group: 'Advanced Controls',
    rows: [
      { label: 'Brand voice controls', base: 'Basic', pro: 'Advanced' },
      { label: 'Internal linking', base: 'Automatic', pro: 'Automatic + custom control' },
    ],
  },
  {
    group: 'Support & Add-ons',
    rows: [
      { label: 'AI credit top-up packs', base: true, pro: true },
      { label: 'Grandfathered pricing', base: 'First 50 users', pro: 'First 50 users' },
    ],
  },
];

/* ─── Cell renderer ──────────────────────────────────────────────────────────── */

function Cell({ value }: { value: CellValue }) {
  if (value === true) {
    return (
      <span className="inline-flex size-5 items-center justify-center rounded-full bg-gray-100 text-gray-500">
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (value === false || value === null) {
    return (
      <span className="inline-flex size-5 items-center justify-center rounded-full bg-gray-50 text-gray-300">
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return <span className="text-sm font-medium text-gray-800">{value}</span>;
}

/* ─── Component ──────────────────────────────────────────────────────────────── */

export function PricingCompareRef() {
  const router = useRouter();
  const { data: session } = useSession();
  const user = useQuery(api.users.getCurrentUser);
  const hasSubscription = user?.subscriptionStatus === 'active';
  const userPlan = user?.plan ?? undefined;
  const hasSession = !!session;

  function getHref(planId: 'base' | 'pro') {
    if (hasSession && hasSubscription) return '/dashboard';
    if (!hasSession) return '/sign-up';
    return '/onboarding?step=7';
  }

  function getLabel(planId: 'base' | 'pro') {
    if (hasSession && hasSubscription) {
      const isCurrent =
        (planId === 'base' && userPlan === 'STARTER') ||
        (planId === 'pro' && userPlan === 'PRO');
      if (isCurrent) return 'Current Plan';
    }
    return planId === 'pro' ? 'Get Started Now' : 'Get Started';
  }

  return (
    <section className="pb-16 sm:pb-24">
      {/* Section heading */}
      <Container className="pt-16 sm:pt-20">
        <div className="mb-12 text-center">
          <h2 className={`${heroDisplay.className} text-3xl font-normal tracking-tight text-gray-900 sm:text-[2.25rem]`}>
            Compare all of our plans
          </h2>
        </div>
      </Container>

      {/* Table — spans edge to edge between rails */}
      <div
        className="rail-inset-x overflow-x-auto border-t border-b border-gray-200"
      >
        <table className="w-full table-fixed border-collapse">
          {/* ── Sticky column header with plan names, prices, CTAs ── */}
          <thead>
            <tr className="border-b border-gray-200">
              {/* Feature label col */}
              <th className="w-[40%] bg-white px-8 py-7 text-left align-bottom">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  Feature
                </p>
              </th>

              {/* Starter plan col */}
              <th className="w-[30%] border-l border-gray-200 bg-white px-8 py-7 text-left align-top relative">
                <p className="text-base font-bold text-gray-900">Starter Plan</p>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-[2.25rem] font-bold leading-none tracking-tight text-gray-900">
                    $15
                  </span>
                  <span className="text-xs font-light text-gray-400">/month</span>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => router.push(getHref('base'))}
                    className="w-full cursor-pointer rounded-md border border-gray-200 bg-white py-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-75 hover:border-gray-300 hover:shadow-md active:translate-y-[2px]"
                  >
                    {getLabel('base')}
                  </button>
                </div>
              </th>

              {/* Pro plan col */}
              <th className="w-[30%] border-l border-gray-200 bg-[#129990]/[0.03] px-8 py-7 text-left align-top relative">
                <p className="text-base font-bold text-gray-900">Pro Plan</p>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-[2.25rem] font-bold leading-none tracking-tight text-gray-900">
                    $25
                  </span>
                  <span className="text-xs font-light text-gray-400">/month</span>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => router.push(getHref('pro'))}
                    className="relative w-full cursor-pointer rounded-md border border-[#129990]/50 bg-gradient-to-r from-[#129990] to-[#1EAD9F] py-2 text-sm font-medium text-white shadow-[inset_0_1px_0px_rgba(255,255,255,0.2),0_0_0_1px_rgba(18,153,144,0.3),0_2px_6px_0_rgba(18,153,144,0.25)] transition-all duration-75 hover:brightness-105 active:translate-y-[2px]"
                  >
                    {getLabel('pro')}
                  </button>
                </div>
              </th>
            </tr>
          </thead>

          {/* ── Body ── */}
          <tbody>
            {FEATURE_GROUPS.map((group) => (
              <React.Fragment key={group.group}>
                {/* Group label row */}
                <tr className="border-t border-gray-200 bg-gray-50/60">
                  <td
                    colSpan={3}
                    className="px-8 py-2.5 text-[10px] font-bold uppercase tracking-widest text-gray-400"
                  >
                    {group.group}
                  </td>
                </tr>

                {/* Feature rows */}
                {group.rows.map((row, ri) => (
                  <tr
                    key={row.label}
                    className={`border-t border-gray-100 ${ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                  >
                    <td className="px-8 py-4 text-sm font-normal text-gray-700">
                      {row.label}
                    </td>
                    <td className="border-l border-gray-100 px-8 py-4">
                      <Cell value={row.base} />
                    </td>
                    <td className="border-l border-gray-100 bg-[#129990]/[0.015] px-8 py-4">
                      <Cell value={row.pro} />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
